
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import DailyNumberDisplayerFilterable from '@/components/reports-v3/components/filterables/DailyNumberDisplayerFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {getTableName, getSqlColumnRef} from '@/worker/db/sqlHelpers';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import IncludeTaxToggle from '@/components/IncludeTaxToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import moment from 'moment';
import settingx from '@/store/modules/settingx';
import {PermissionsGroup} from '@/store/models.def';

@Component({
	components: {
		DailyNumberDisplayerFilterable,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		IncludeTaxToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class DashboardSalesNumberDisplayer extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['dashboard'];
    }

	public todayAmount = 0;
	public yesterdayAmount = 0;
	public bracketAmount: number = 0;

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get includeTax() {
		return settingx.now.includeTax;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const { selectedAsOfDate, includeCreditNote, includeDebitNote, includeTax, slPhModuleOnly } = this;
		return JSON.stringify([selectedAsOfDate, includeCreditNote, includeDebitNote, includeTax, slPhModuleOnly]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		const todayStart = moment(this.selectedAsOfDate).startOf('day').valueOf();
		const todayEnd = moment(this.selectedAsOfDate).endOf('day').valueOf();
		const sevenDaysAgo = moment(this.selectedAsOfDate).add(-7, 'day').startOf('day').valueOf();

		const amountKey = this.slPhModuleOnly
			? (this.includeTax ? 'amountWithTax' : 'amount')
			: (this.includeTax ? 'arAmountWithTax' : 'arAmount');
		const amountCol = getSqlColumnRef('dashboardSales', amountKey);
		const table = getTableName('dashboardSales');

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['dashboardSales']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.6);

		const [todayResult, weekResult] = await Promise.all([
			queryHead.rawQuery(`
				SELECT
					SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN TRY_CAST(${amountCol} AS DOUBLE) ELSE 0 END) as sales,
					SUM(CASE WHEN "DOCTYPE" = 'DN' THEN TRY_CAST(${amountCol} AS DOUBLE) ELSE 0 END) as dn,
					SUM(CASE WHEN "DOCTYPE" = 'CN' THEN TRY_CAST(${amountCol} AS DOUBLE) ELSE 0 END) as cn,
					CAST(SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN 1 ELSE 0 END) AS INTEGER) as sales_cnt,
					CAST(SUM(CASE WHEN "DOCTYPE" = 'DN' THEN 1 ELSE 0 END) AS INTEGER) as dn_cnt,
					CAST(SUM(CASE WHEN "DOCTYPE" = 'CN' THEN 1 ELSE 0 END) AS INTEGER) as cn_cnt
				FROM ${table}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${todayStart} AND ${todayEnd}
			`),
			queryHead.rawQuery(`
				SELECT
					SUM(CASE WHEN "DOCTYPE" IN ('IV','CS') THEN TRY_CAST(${amountCol} AS DOUBLE) ELSE 0 END) as sales,
					SUM(CASE WHEN "DOCTYPE" = 'DN' THEN TRY_CAST(${amountCol} AS DOUBLE) ELSE 0 END) as dn,
					SUM(CASE WHEN "DOCTYPE" = 'CN' THEN TRY_CAST(${amountCol} AS DOUBLE) ELSE 0 END) as cn
				FROM ${table}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${sevenDaysAgo} AND ${todayEnd}
			`),
		]);
		this.checkInterrupted(jobId);

		const today = todayResult[0] || {};
		const week = weekResult[0] || {};

		this.todayAmount = (Number(today.sales) || 0)
			+ (this.includeDebitNote ? (Number(today.dn) || 0) : 0)
			- (this.includeCreditNote ? (Number(today.cn) || 0) : 0);

		const weekTotal = (Number(week.sales) || 0)
			+ (this.includeDebitNote ? (Number(week.dn) || 0) : 0)
			- (this.includeCreditNote ? (Number(week.cn) || 0) : 0);
		this.yesterdayAmount = weekTotal / 7;

		this.bracketAmount = (Number(today.sales_cnt) || 0)
			+ (this.includeDebitNote ? (Number(today.dn_cnt) || 0) : 0)
			+ (this.includeCreditNote ? (Number(today.cn_cnt) || 0) : 0);

		this.generateLoadingText(1);
		this.saveHistory('todayAmount', 'yesterdayAmount', 'bracketAmount');
	}
}
